import { decode } from 'he';
import { useRef, useState } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import {
  linkControlledHoverEffect,
  useMatchBreakpoint,
} from '@hivebrite/design-system/Theme';
import { onEnterOrSpacePress } from '@hivebrite/utils';

import { htmlToString } from 'hb-react/frontoffice/utils/globalUtils';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';

import type { NewsCardProps } from './types';

export const NewsCard = ({
  coverPicture,
  description,
  fallbackCoverPicture,
  onClick,
  publishedAt,
  subject,
}: NewsCardProps) => {
  const isXL = useMatchBreakpoint('xl');
  const cardRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const cleanDesc = decode(htmlToString(description));

  return (
    <CardStyled
      onClick={onClick}
      onKeyPress={onEnterOrSpacePress(onClick)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      ref={cardRef}
      role="link"
      tabIndex={0}
    >
      <ImgWrapper dataTestId="news-cover-wrapper">
        <ImgStyled
          alt={subject}
          as="img"
          onError={(e) => {
            if (fallbackCoverPicture) {
              (e.target as HTMLImageElement).src = fallbackCoverPicture;
            }
          }}
          src={coverPicture}
        />
      </ImgWrapper>

      <ContentWrapper>
        <AnimatedContentBox>
          <Box data-testid="link-to-news">
            <ScreenReadersContent>
              {s__('Accessibility|Click to see the news %{subject}').i__({
                subject,
              })}
            </ScreenReadersContent>
            <LinkStyled>
              <ClampedLinkWorkaround
                clamp={isHovered || isXL ? 2 : 1}
                lineHeight="2rem"
                margin={0}
                padding={0}
                title={subject}
                useNativeClamp
              >
                {subject}
              </ClampedLinkWorkaround>
            </LinkStyled>
          </Box>

          {publishedAt && (
            <DateStyled $description={cleanDesc}>{publishedAt}</DateStyled>
          )}

          {cleanDesc && (
            <Description font="body-regular-asphalt" ref={descRef}>
              <Dotdotdot clamp={isHovered ? 4 : 2} useNativeClamp>
                {cleanDesc}
              </Dotdotdot>
            </Description>
          )}
        </AnimatedContentBox>
      </ContentWrapper>
    </CardStyled>
  );
};

const commonContentStyles = {
  pb: 6,
  pt: 3,
};

const ContentWrapper = styled(Box).attrs({
  ...commonContentStyles,
  height: { _: '92px', xl: '118px' },
})`
  // Inherit Card component's background
  background: inherit;
  box-sizing: content-box;
  border-radius: ${({ theme }) => {
    const br = theme.themeVariables.globalBorderRadius;

    return `0 0 ${br}px ${br}px`;
  }};
`;

const AnimatedContentBox = styled(Box).attrs(() => ({
  ...commonContentStyles,
  height: { _: '92px', xl: '118px' },
  flex: 1,
  flexDirection: 'column',
  px: { _: 3, l: 4 },
  zIndex: 1,
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  overflow: 'hidden',
}))`
  // Inherit Card component's background
  background: inherit;
  transition: height 0.3s;
  box-sizing: content-box;
  border-radius: ${({ theme }) => {
    const br = theme.themeVariables.globalBorderRadius;

    return `0 0 ${br}px ${br}px`;
  }};
`;

const ImgStyled = styled(Box).attrs({
  width: '100%',
})`
  // Inherit Card component's background
  background: inherit;
  cursor: pointer;
  object-fit: cover;
  transition-duration: 0.3s;
  transition-property: filter, height, transform;
  aspect-ratio: 1600 / 600;
`;

const ImgWrapper = styled(Box).attrs({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
})`
  // Inherit Card component's background
  background: inherit;
  background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
  border-radius: ${({ theme }) => {
    const br = theme.themeVariables.globalBorderRadius;

    return `${br}px ${br}px 0 0`;
  }};
`;

const LinkStyled = styled(Box).attrs({
  as: 'h3',
  font: 'h3-semibold',
  marginTop: '0 !important', // Overwrite PO h3 style that breaks the card layout
  marginBottom: '0 !important', // Overwrite PO h3 style that breaks the card layout
})`
  text-decoration: none;
`;

const DateStyled = styled(Box).attrs<{ $description?: string }>(
  ({ $description }) => ({
    font: 'body-semibold-asphalt',
    mt: 1,
    pb: $description && '6px',
  }),
)<{ $description?: string }>``;

const Description = styled(Box)`
  line-height: 1.5rem;
`;

const CardStyled = styled(Card)`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;

  @media (hover: hover) {
    & ${ImgStyled} {
      transform: scale(1);
    }

    &:hover ${ImgStyled} {
      transform: scale(1.05);
    }

    &:hover ${LinkStyled} {
      ${linkControlledHoverEffect()}
    }

    &:hover ${AnimatedContentBox} {
      height: 157px;
    }
  }
`;
